import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { useNativePlatform } from "@/hooks/useNativePlatform";
import { isNativeParentSheetOpen } from "@/lib/native/nativeNestedParent";
import { NativeOptionSheet, type NativeOption } from "@/components/native/NativeOptionSheet";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

type NativeSelectFieldProps = {
  value: string;
  onValueChange: (value: string) => void;
  options: NativeOption[];
  title: string;
  placeholder?: string;
  triggerClassName?: string;
  /** Optional icon or markup before label in web select items */
  renderOptionLabel?: (option: NativeOption) => React.ReactNode;
};

/** Native: bottom sheet picker. Web: Radix Select. */
export const NativeSelectField = ({
  value,
  onValueChange,
  options,
  title,
  placeholder,
  triggerClassName,
  renderOptionLabel,
}: NativeSelectFieldProps) => {
  const [open, setOpen] = useState(false);
  const { isNative } = useNativePlatform();
  const selected = options.find((o) => o.value === value);

  if (isNative) {
    return (
      <>
        <button
          type="button"
          onClick={() => setOpen(true)}
          className={cn(
            "flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm",
            triggerClassName,
          )}
        >
          <span className="truncate text-left">
            {selected?.label ?? placeholder ?? title}
          </span>
          <ChevronDown className="h-4 w-4 shrink-0 opacity-70" />
        </button>
        <NativeOptionSheet
          open={open}
          onOpenChange={setOpen}
          title={title}
          value={value}
          options={options}
          onSelect={onValueChange}
          nestedSheet={isNativeParentSheetOpen()}
        />
      </>
    );
  }

  return (
    <Select value={value} onValueChange={onValueChange}>
      <SelectTrigger className={triggerClassName}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {options.map((opt) => (
          <SelectItem key={opt.value} value={opt.value}>
            {renderOptionLabel ? renderOptionLabel(opt) : opt.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};
